import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MarquardtMask } from "@/components/MarquardtMask";

const MANIFESTO_IMG = "/images/barber-unsplash.jpg";

const CHAPTERS = [
  {
    num: "01",
    title: "Маска Марквардта",
    text: "На ваше фото накладывается маска эталонных пропорций — та самая, которой пользуются пластические хирурги и стилисты. Сразу видно, где лицо совпадает с каноном, а где нет.",
  },
  {
    num: "02",
    title: "Персональный анализ",
    text: "Система анализирует ключевые ориентиры лица и помогает изучить его форму и соотношение отдельных зон. Получите структурированный разбор вместо общих рекомендаций.",
  },
  {
    num: "03",
    title: "Ваша стрижка",
    text: "Примеряйте разные варианты причёсок на собственном фото и сравнивайте результаты. Так вы сможете заранее определиться с направлением будущей стрижки.",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 48 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 1, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section
      id="manifesto"
      ref={ref}
      className="relative overflow-hidden px-6 py-28 md:px-12 md:py-40 lg:px-24"
      data-testid="manifesto-section"
    >
      {/* Фон: золотое свечение + водяной знак (общая фактура лица — на уровне всей страницы) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-32 top-10 h-96 w-96 rounded-full bg-[#C2A878]/[0.05] blur-[120px]"
      />
      <div className="pointer-events-none absolute -right-40 top-1/2 w-[340px] -translate-y-1/2 opacity-[0.05] lg:w-[520px] lg:opacity-[0.06]">
        <MarquardtMask breathing={false} />
      </div>

      <div className="relative grid gap-16 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-5">
          <motion.p
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            custom={0}
            className="mb-8 font-jetbrains text-xs uppercase tracking-[0.35em] text-[#C2A878]"
          >
            ( Манифест )
          </motion.p>
          <motion.h2
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            custom={1}
            className="font-cormorant text-4xl font-light leading-[1.05] tracking-tight text-[#F4F4F0] md:text-6xl"
          >
            Красоту можно измерить
          </motion.h2>

          <motion.div
            style={{ y: imageY }}
            className="relative mt-16 hidden lg:block"
          >
            <div className="overflow-hidden border border-white/10">
              <img
                src={MANIFESTO_IMG}
                alt="Кресло в тёмном барбершопе"
                className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
            <span className="absolute -bottom-5 right-6 font-jetbrains text-xs uppercase tracking-[0.3em] text-[#525252]">
              Кадр / 01
            </span>
          </motion.div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          {CHAPTERS.map((chapter, i) => (
            <motion.article
              key={chapter.num}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              custom={i}
              className="group border-t border-white/10 py-12 last:border-b"
              data-testid={`manifesto-chapter-${chapter.num}`}
            >
              <div className="flex items-start gap-8">
                <span className="font-jetbrains text-sm tracking-[0.2em] text-[#C2A878] transition-colors duration-500">
                  {chapter.num}
                </span>
                <div>
                  <h3 className="font-cormorant text-3xl font-light text-[#F4F4F0] transition-colors duration-500 group-hover:text-[#C2A878] md:text-4xl">
                    {chapter.title}
                  </h3>
                  <p className="mt-5 max-w-lg text-base leading-relaxed text-[#A3A3A3]">
                    {chapter.text}
                  </p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
